import React, { useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { 
  transformControlStandardsForChart
} from '../../../../utils/qaqc/chartDataTransform';
import {
  augmentRowsZScorePlot,
  Z_SCORE_AXIS_MAX,
  Z_SCORE_AXIS_MIN
} from '../../../../utils/qaqc/chartOutliers';
import { PRINT_COLORS, PRINT_CHART_CONFIG } from '../printTheme';
import PrintChartLegendContent from '../PrintChartLegendContent';

const PrintControlStandardsChart = ({ data, element, crmCode = 'all' }) => {
  // Transform data for chart
  const chartData = useMemo(() => {
    return transformControlStandardsForChart(data, element, crmCode);
  }, [data, element, crmCode]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (chartData.length === 0) return null;
    
    const failures = chartData.filter(d => !d.within3sd).length;
    const warnings = chartData.filter(d => !d.within2sd && d.within3sd).length;
    const passes = chartData.filter(d => d.within2sd).length;
    
    return {
      count: chartData.length,
      failures,
      warnings,
      passes,
      passRate: ((passes / chartData.length) * 100).toFixed(1)
    };
  }, [chartData]);

  if (chartData.length === 0) {
    return (
      <div style={{ width: '100%', padding: '20px', background: PRINT_COLORS.background }}>
        <h3 style={{ color: PRINT_COLORS.text, textAlign: 'center' }}>
          Control Standards - {element} {crmCode !== 'all' ? `(${crmCode})` : ''}
        </h3>
        <p style={{ color: PRINT_COLORS.text, textAlign: 'center' }}>No data available</p>
      </div>
    );
  }

  const plotData = useMemo(
    () => augmentRowsZScorePlot(chartData, 'zScore'),
    [chartData]
  );

  const renderDot = (props) => {
    const { cx, cy, payload } = props;
    if (!payload) return null;
    
    let color = PRINT_COLORS.pass;
    if (!payload.within3sd) color = PRINT_COLORS.fail;
    else if (!payload.within2sd) color = PRINT_COLORS.warning;
    
    const size = !payload.within3sd ? 8 : (!payload.within2sd ? 6 : 5);
    const clip = payload.zScoreAxisLabel;
    const labelBelow =
      clip && payload.zScore > (payload.plotZScore ?? payload.zScore);
    
    return (
      <g>
        <circle 
          cx={cx} 
          cy={cy} 
          r={size} 
          fill={color}
          stroke={!payload.within3sd ? PRINT_COLORS.text : color}
          strokeWidth={!payload.within3sd ? 1 : 0}
        />
        {clip && (
          <text
            x={cx}
            y={labelBelow ? cy + size + 9 : cy - size - 3}
            fill={PRINT_COLORS.text}
            fontSize={8}
            textAnchor="middle"
          >
            {clip}
          </text>
        )}
      </g>
    );
  };

  return (
    <div style={{ width: '100%', padding: '20px', background: PRINT_COLORS.background }}>
      <div style={{ marginBottom: '15px' }}>
        <h3 style={{ color: PRINT_COLORS.text, marginBottom: '5px', fontSize: '16px', fontWeight: 'bold' }}>
          Control Standards - {element} {crmCode !== 'all' ? `(${crmCode})` : ''}
        </h3>
        {stats && (
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            fontSize: '10px', 
            color: PRINT_COLORS.text,
            borderBottom: `1px solid ${PRINT_COLORS.border}`,
            paddingBottom: '8px'
          }}>
            <span><strong>Total:</strong> {stats.count}</span>
            <span style={{ color: PRINT_COLORS.pass }}><strong>Pass:</strong> {stats.passes} ({stats.passRate}%)</span>
            <span style={{ color: PRINT_COLORS.warning }}><strong>Warning:</strong> {stats.warnings}</span>
            <span style={{ color: PRINT_COLORS.fail }}><strong>Fail:</strong> {stats.failures}</span>
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <ScatterChart margin={PRINT_CHART_CONFIG.margin}>
          <CartesianGrid strokeDasharray="3 3" stroke={PRINT_COLORS.grid} />
          
          <XAxis 
            type="number"
            dataKey="index"
            stroke={PRINT_COLORS.text}
            tick={{ fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.tick }}
            height={56}
            label={{ 
              value: 'Sample Order (by Date)', 
              position: 'insideBottom', 
              offset: 20,
              style: { fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.axis, fontWeight: 600 }
            }}
          />
          
          <YAxis 
            type="number"
            stroke={PRINT_COLORS.text}
            tick={{ fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.tick }}
            label={{ 
              value: 'Z-Score', 
              angle: -90, 
              position: 'insideLeft',
              style: { fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.axis }
            }}
            domain={[Z_SCORE_AXIS_MIN, Z_SCORE_AXIS_MAX]}
            allowDataOverflow
            tickFormatter={(value) => value.toFixed(2)}
          />
          
          <ReferenceLine 
            y={0} 
            stroke={PRINT_COLORS.referenceLineZero} 
            strokeWidth={PRINT_CHART_CONFIG.strokeWidth.normal}
            label={{ value: 'Target', fill: PRINT_COLORS.text, fontSize: 8 }}
          />
          <ReferenceLine 
            y={2} 
            stroke={PRINT_COLORS.referenceLineWarn} 
            strokeDasharray="5 5"
            label={{ value: '+2SD', fill: PRINT_COLORS.warning, fontSize: 8, position: 'right' }}
          />
          <ReferenceLine 
            y={-2} 
            stroke={PRINT_COLORS.referenceLineWarn} 
            strokeDasharray="5 5"
            label={{ value: '-2SD', fill: PRINT_COLORS.warning, fontSize: 8, position: 'right' }}
          />
          <ReferenceLine 
            y={3} 
            stroke={PRINT_COLORS.referenceLineFail} 
            strokeDasharray="3 3"
            strokeWidth={PRINT_CHART_CONFIG.strokeWidth.thick}
            label={{ value: '+3SD', fill: PRINT_COLORS.fail, fontSize: 8, position: 'right' }}
          />
          <ReferenceLine 
            y={-3} 
            stroke={PRINT_COLORS.referenceLineFail} 
            strokeDasharray="3 3"
            strokeWidth={PRINT_CHART_CONFIG.strokeWidth.thick}
            label={{ value: '-3SD', fill: PRINT_COLORS.fail, fontSize: 8, position: 'right' }}
          />
          
          <Scatter 
            data={plotData}
            dataKey="plotZScore"
            shape={renderDot}
            name="CRM Z-Scores"
          />
          
          <Legend content={() => (
            <PrintChartLegendContent
              payload={[
                { value: 'Pass (±2SD)', type: 'circle', color: PRINT_COLORS.pass },
                { value: 'Warning (>2SD)', type: 'circle', color: PRINT_COLORS.warning },
                { value: 'Fail (>3SD)', type: 'circle', color: PRINT_COLORS.fail }
              ]}
            />
          )} />
        </ScatterChart>
      </ResponsiveContainer>

      <div style={{ 
        marginTop: '2px', 
        fontSize: '8px', 
        color: PRINT_COLORS.text,
        fontStyle: 'italic'
      }}>
        Control standards (CRMs) validate analytical accuracy. Z-scores show deviation from certified values.
      </div>
    </div>
  );
};

export default PrintControlStandardsChart;
